/**
 * Trend consumi mensili + CAGR (SLIMS-CER-037).
 *
 * Parte dalla serie oraria riconciliata di `energy_shared` (GSE > reale > stimato > tipico) e la aggrega per mese solare. Le pagine che mostrano il trend
 * hanno già quella serie in memoria: qui non si fa I/O, solo aggregazione.
 *
 * Sul confronto anno su anno: i consumi elettrici sono fortemente stagionali
 * (riscaldamento/condizionamento), quindi confrontare due mesi adiacenti o il
 * primo con l'ultimo di una finestra da 12 misura la stagione, non la crescita.
 * Tutti i confronti qui sono **destagionalizzati per costruzione**: si mettono a
 * confronto solo mesi distanti un multiplo esatto di 12, cioè lo stesso mese
 * solare di anni diversi.
 */

const MONTH_LABELS = [
	'gen',
	'feb',
	'mar',
	'apr',
	'mag',
	'giu',
	'lug',
	'ago',
	'set',
	'ott',
	'nov',
	'dic',
] as const;

/** Un bucket della serie oraria riconciliata (`GET /api/energy/shared`). */
export interface ConsumptionTrendInput {
	hour: string | Date;
	totalImmessa: number;
	totalPrelevata: number;
	energiaCondivisa: number;
}

export interface ConsumptionTrendMonth {
	/** Chiave ordinabile `YYYY-MM`. */
	key: string;
	year: number;
	/** Mese solare 1-12. */
	month: number;
	/** Etichetta breve per l'asse X, es. `lug 26`. */
	label: string;
	/** Consumo (energia prelevata) del mese, kWh. */
	prelevata: number;
	immessa: number;
	condivisa: number;
	/** Consumo dello stesso mese dell'anno precedente, se presente nello storico. */
	prevYearPrelevata: number | null;
	/** Variazione % sullo stesso mese dell'anno precedente. */
	yoyPct: number | null;
}

export interface ConsumptionYoY {
	/** Somma dei mesi confrontabili nella finestra recente, kWh. */
	currentKwh: number;
	/** Somma degli stessi mesi solari un anno prima, kWh. */
	previousKwh: number;
	deltaPct: number;
	/** Mesi presenti in entrambe le finestre: sotto 12 il confronto è parziale. */
	matchedMonths: number;
}

export interface ConsumptionTrend {
	/** Ultimi N mesi solari con dato, in ordine cronologico. */
	months: ConsumptionTrendMonth[];
	/** Mesi distinti con dato in tutto lo storico. */
	monthsAvailable: number;
	/** Consumo totale della finestra mostrata, kWh. */
	totalKwh: number;
	/** Consumo medio mensile della finestra mostrata, kWh. */
	avgKwh: number;
	/** Mese di consumo massimo nella finestra, se la finestra non è vuota. */
	peak: ConsumptionTrendMonth | null;
	/** CAGR % sui consumi, `null` se lo storico non copre due annualità intere. */
	cagrPct: number | null;
	/** Annualità su cui il CAGR è composto (l'esponente 1/n). */
	cagrYears: number | null;
	/** Confronto anno su anno sull'ultima annualità, `null` se non confrontabile. */
	yoy: ConsumptionYoY | null;
}

export interface ConsumptionTrendOptions {
	/** Ampiezza della finestra mostrata nel grafico. Default 12 mesi. */
	windowMonths?: number;
}

/** `2026-07` da anno e mese solare. */
function monthKey(year: number, month: number): string {
	return `${year}-${String(month).padStart(2, '0')}`;
}

/** Indice assoluto in mesi, per fare aritmetica sulle chiavi senza `Date`. */
function monthIndex(year: number, month: number): number {
	return year * 12 + (month - 1);
}

function keyFromIndex(index: number): string {
	return monthKey(Math.floor(index / 12), (index % 12) + 1);
}

interface Bucket {
	immessa: number;
	prelevata: number;
	condivisa: number;
}

/**
 * Aggrega la serie oraria per mese e ne ricava trend, CAGR e confronto YoY.
 *
 * I bucket vuoti non vengono inventati: un mese senza misure resta assente e
 * viene escluso dai confronti, invece di entrarci come zero (che leggerebbe
 * come "consumo crollato" anziché "dato mancante").
 */
export function buildConsumptionTrend(
	points: readonly ConsumptionTrendInput[],
	options: ConsumptionTrendOptions = {},
): ConsumptionTrend {
	const windowMonths = Math.max(1, Math.trunc(options.windowMonths ?? 12));
	const buckets = new Map<string, Bucket>();

	for (const point of points) {
		const date = point.hour instanceof Date ? point.hour : new Date(point.hour);
		if (Number.isNaN(date.getTime())) continue;
		const key = monthKey(date.getFullYear(), date.getMonth() + 1);
		const bucket = buckets.get(key) ?? { immessa: 0, prelevata: 0, condivisa: 0 };
		bucket.immessa += point.totalImmessa;
		bucket.prelevata += point.totalPrelevata;
		bucket.condivisa += point.energiaCondivisa;
		buckets.set(key, bucket);
	}

	// `YYYY-MM` si ordina lessicograficamente come cronologicamente.
	const entries = [...buckets.entries()].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
	if (entries.length === 0) {
		return {
			months: [],
			monthsAvailable: 0,
			totalKwh: 0,
			avgKwh: 0,
			peak: null,
			cagrPct: null,
			cagrYears: null,
			yoy: null,
		};
	}

	const prelevataAt = (key: string): number | null => buckets.get(key)?.prelevata ?? null;

	const months: ConsumptionTrendMonth[] = entries.slice(-windowMonths).map(([key, bucket]) => {
		const [yearPart, monthPart] = key.split('-');
		const year = Number(yearPart);
		const month = Number(monthPart);
		const prevYear = prelevataAt(monthKey(year - 1, month));
		return {
			key,
			year,
			month,
			label: `${MONTH_LABELS[month - 1]} ${String(year % 100).padStart(2, '0')}`,
			prelevata: Math.round(bucket.prelevata),
			immessa: Math.round(bucket.immessa),
			condivisa: Math.round(bucket.condivisa),
			prevYearPrelevata: prevYear === null ? null : Math.round(prevYear),
			yoyPct:
				prevYear !== null && prevYear > 0
					? Math.round(((bucket.prelevata - prevYear) / prevYear) * 1000) / 10
					: null,
		};
	});

	const totalKwh = months.reduce((sum, m) => sum + m.prelevata, 0);
	const peak = months.reduce<ConsumptionTrendMonth | null>(
		(best, m) => (best === null || m.prelevata > best.prelevata ? m : best),
		null,
	);

	const firstIndex = indexOfKey(entries[0][0]);
	const lastIndex = indexOfKey(entries[entries.length - 1][0]);

	return {
		months,
		monthsAvailable: entries.length,
		totalKwh,
		avgKwh: months.length > 0 ? Math.round(totalKwh / months.length) : 0,
		peak,
		...computeCagr(buckets, firstIndex, lastIndex),
		yoy: computeYoY(buckets, lastIndex),
	};
}

function indexOfKey(key: string): number {
	const [year, month] = key.split('-');
	return monthIndex(Number(year), Number(month));
}

/**
 * Somma due finestre da 12 mesi distanti `yearsApart` anni, contando solo le
 * coppie in cui **entrambi** i mesi hanno dato. Una finestra base con dei buchi
 * confrontata con una recente piena produrrebbe una crescita inesistente.
 */
function matchWindows(
	buckets: Map<string, Bucket>,
	recentEndIndex: number,
	yearsApart: number,
): { current: number; previous: number; matched: number } {
	let current = 0;
	let previous = 0;
	let matched = 0;
	for (let offset = 0; offset < 12; offset++) {
		const recent = buckets.get(keyFromIndex(recentEndIndex - offset));
		const base = buckets.get(keyFromIndex(recentEndIndex - offset - 12 * yearsApart));
		if (!recent || !base) continue;
		current += recent.prelevata;
		previous += base.prelevata;
		matched++;
	}
	return { current, previous, matched };
}

/** Confronto anno su anno sull'ultima annualità disponibile. */
function computeYoY(buckets: Map<string, Bucket>, lastIndex: number): ConsumptionYoY | null {
	const { current, previous, matched } = matchWindows(buckets, lastIndex, 1);
	if (matched === 0 || previous <= 0) return null;
	return {
		currentKwh: Math.round(current),
		previousKwh: Math.round(previous),
		deltaPct: Math.round(((current - previous) / previous) * 1000) / 10,
		matchedMonths: matched,
	};
}

/**
 * CAGR sui consumi: `(recente / base)^(1/anni) - 1`, dove le due finestre sono
 * da 12 mesi e distano un numero **intero** di anni — così coprono gli stessi
 * mesi solari e la stagionalità si annulla.
 *
 * Serve quindi almeno uno storico di 24 mesi: sotto quella soglia non esiste un
 * tasso annuo osservabile e si torna `null` invece di annualizzare una
 * variazione infra-annuale (che su dati stagionali è rumore amplificato).
 */
function computeCagr(
	buckets: Map<string, Bucket>,
	firstIndex: number,
	lastIndex: number,
): { cagrPct: number | null; cagrYears: number | null } {
	// Anni interi che separano l'inizio della finestra recente (lastIndex - 11)
	// dal primo mese con dato: quante volte si può indietreggiare di 12 mesi
	// restando dentro lo storico.
	const years = Math.floor((lastIndex - 11 - firstIndex) / 12);
	if (years < 1) return { cagrPct: null, cagrYears: null };

	const { current, previous, matched } = matchWindows(buckets, lastIndex, years);
	if (matched === 0 || previous <= 0 || current <= 0) return { cagrPct: null, cagrYears: null };

	const cagr = (current / previous) ** (1 / years) - 1;
	return { cagrPct: Math.round(cagr * 1000) / 10, cagrYears: years };
}
